import React, { useState } from 'react'
import { CircularProgress } from '@mui/material'
import { Download } from '@mui/icons-material'

import { toolbarButton } from '../../instructorDashboard/components/dashboardStyles'
import { showToast } from '../../commonUnit/Toast'
import { downloadExperimentDataset, downloadOffloadingDataset, usesOffloading } from './experimentExport'

// The experiment's CSV exports, one file for every condition: the Monitor's session-level and
// query-level exports joined with a condition column, and the offloading analysis when a
// condition runs it. `conditions` are the config's conditions, each with its `row` when the
// session still exists; only those are exported. For the Overview, which shows nothing from the
// AI's labels and tells no condition apart: `withOffloading={false}` leaves the offloading
// analysis out - no Offloading CSV, and no offloading columns in the Session-level CSV, so its
// summary is not fetched - and `withConditionNames={false}` keeps the name of a condition whose
// data failed to load out of the error message and the console.
const ExperimentExportButtons = ({ experimentId, experimentName, conditions, withOffloading = true, withConditionNames = true }) => {
  const [exporting, setExporting] = useState(null)
  const live = conditions.filter((condition) => condition.row)

  const exportAll = async (level) => {
    setExporting(level)
    try {
      const count =
        level === 'offloading'
          ? await downloadOffloadingDataset({ experimentId, experimentName, conditions: live })
          : await downloadExperimentDataset({ experimentId, experimentName, conditions: live, level, withOffloading })
      if (!count) {
        showToast(
          level === 'offloading'
            ? 'Nothing to export yet: no chat exchange has been analyzed.'
            : 'Nothing to export yet: no student has activity in any condition.',
          'info',
          3000
        )
      }
    } catch (error) {
      // The Overview names no condition, its console included: the error carries the name.
      console.error('Experiment export failed:', withConditionNames ? error : error?.message)
      const condition = withConditionNames && error.conditionName ? ` (${error.conditionName})` : ''
      showToast(`Could not build the export${condition}. Try again in a moment.`, 'error', 4000)
    } finally {
      setExporting(null)
    }
  }

  return (
    <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
      {[
        ['session', 'Session-level CSV'],
        ['query', 'Query-level CSV'],
        ...(withOffloading && usesOffloading(conditions) ? [['offloading', 'Offloading CSV']] : []),
      ].map(([level, label]) => (
        <button
          key={level}
          type="button"
          style={{ ...toolbarButton, opacity: exporting || !live.length ? 0.6 : 1 }}
          disabled={Boolean(exporting) || !live.length}
          title={
            level === 'offloading'
              ? 'Every analyzed chat exchange: which concepts the student asked the AI about, or worked through, and why'
              : "Each condition's Monitor export, joined into one file with a condition column"
          }
          onClick={() => exportAll(level)}>
          {exporting === level ? <CircularProgress size={12} /> : <Download sx={{ fontSize: 14 }} />}
          {label}
        </button>
      ))}
    </div>
  )
}

export default ExperimentExportButtons
